import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import PageHeader from 'Components/PageHeader/PageHeader';
import PeriodsHeader from '~/Components/PeriodsHeader/PeriodsHeader';

import { dashboardStatsQueryOptions } from 'Providers/DashboardStatsProvider';
import {
  EGlobalStateCases,
  useGlobalState,
} from 'Providers/GlobalStateProvider';

import { FormattedMessage } from 'react-intl';
import AvailabilitySection from 'Components/AvailabilitySection/AvailabilitySection';
import { LiftStatusStorage } from 'Utils/Storage';
import { SmallCardsGrid } from '../../Components/SmallCardsGrid/SmallCardsGrid';
import classes from './Dashboard.module.scss';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import useCallReportService from 'Hooks/useCallReportService.hook';
import { useQuery } from '@tanstack/react-query';
import { useUser } from 'Providers/UserProvider';
import { useSelectedOrganization } from 'Providers/OrganizationProvider';
import { ApiError } from '~/lib/api-client';
import { liftSearchQueryOptions } from '~/features/lifts/query-options';

const useTotalLifts = () => {
  const { selectedOrganization } = useSelectedOrganization();

  const totalLiftsQuery = useQuery({
    ...liftSearchQueryOptions({
      limit: 1,
      organisationId: selectedOrganization?.id,
    }),
    select: (data) => data.total,
  });

  return {
    totalLifts: totalLiftsQuery.data,
    liftCountResolved: totalLiftsQuery.status !== 'pending',
  };
};

const InnerDashboard = () => {
  const [globalState, globalStateDispatch] = useGlobalState();

  const { liftCountResolved, totalLifts } = useTotalLifts();

  const user = useUser();

  const { selectedOrganization } = useSelectedOrganization();

  const dashboardStatsQuery = useQuery(
    dashboardStatsQueryOptions({
      viewIsCurrent: globalState.viewIsCurrent,
      organisationId: selectedOrganization?.id,
      from: LiftStatusStorage.startDate ?? undefined,
      to: LiftStatusStorage.endDate ?? undefined,
      startHour: 7,
      endHour: 19,
    })
  );

  // Availability stats only exist on the year-view (stats) response
  const yearStats = globalState.viewIsCurrent
    ? undefined
    : (dashboardStatsQuery.data as
        LiftStatus.Schemas.IStatsOverviewDto | undefined);

  const isPending =
    dashboardStatsQuery.isPending ||
    (!dashboardStatsQuery.isError && !liftCountResolved);

  if (
    (liftCountResolved && totalLifts === 0 && !dashboardStatsQuery.isPending) ||
    (dashboardStatsQuery.error instanceof ApiError &&
      dashboardStatsQuery.error.status === 404) // No lifts found
  ) {
    return (
      <Typography variant="subtitle1">
        <FormattedMessage id="organisation.has.no.lifts" />
      </Typography>
    );
  }

  if (dashboardStatsQuery.isError) {
    return <p>{dashboardStatsQuery.error.message}</p>;
  }

  return (
    <div className={classes.container}>
      {!user.isClientLight && !user.isMechanic && (
        <div className={classes.periods}>
          <PeriodsHeader
            hideNavigation
            onDateChange={(dateRange) => {
              globalStateDispatch({
                type: EGlobalStateCases.setDatesLocal,
                payload: {
                  startDate: dateRange.from,
                  endDate: dateRange.to,
                },
              });
            }}
          />
        </div>
      )}

      {isPending ? (
        <div className={classes.progressIndicator}>
          <CircularProgress color="secondary" />
        </div>
      ) : (
        <PageHeader
          title="Overzicht"
          subtitle={
            <>
              <FormattedMessage id="total.lifts" />: {totalLifts}
            </>
          }
          actions={
            !globalState.viewIsCurrent &&
            selectedOrganization?.id && (
              <GenerateReportButton organizationId={selectedOrganization.id} />
            )
          }
        />
      )}

      <SmallCardsGrid
        liftStats={dashboardStatsQuery.data ?? {}}
        loading={dashboardStatsQuery.isPending}
        viewIsCurrent={globalState.viewIsCurrent}
      />

      {!globalState.viewIsCurrent && (
        <AvailabilitySection
          loading={dashboardStatsQuery.isPending}
          liftAvailability={yearStats?.liftAvailability}
          intercomAvailability={yearStats?.intercomAvailability}
        />
      )}
    </div>
  );
};

export default function Dashboard() {
  const { selectedOrganization } = useSelectedOrganization();

  if (!selectedOrganization) {
    return (
      <Box className={classes.noorganisation}>
        <Typography variant="subtitle1">
          <FormattedMessage id="first.select.an.organisation" />
        </Typography>
      </Box>
    );
  }

  return <InnerDashboard />;
}

function GenerateReportButton({ organizationId }: { organizationId: number }) {
  const [reportLoading, fetchReport] = useCallReportService({
    subjectId: organizationId,
    report: 'organisation',
  });

  if (reportLoading) {
    return <CircularProgress size={24} />;
  }

  return (
    <IconButton disabled={reportLoading} onClick={fetchReport} size="small">
      <PictureAsPdfIcon />
    </IconButton>
  );
}
